import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarRange, Check, Loader2, MessageCircleQuestion } from "lucide-react";

import { BROKER_ALERT_TITLE_CLASS } from "@/components/Broker/brokerAlertUi";
import { FieldSelectFullWidth, type TradeFilterSelectOption } from "@/components/trade-filter-selects";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { type ColumnDef, type PaginationState } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { TradeLogSortableCell } from "../TradeLog/components/tradeLogSortableCell";
import { cn } from "@/lib/utils";
import {
  listStrategyRdAccountPresets,
  runStrategyRdAccountTest,
  type StrategyRdAccountBalanceResult,
  type StrategyRdAccountPreset,
  type StrategyRdAccountResult,
  type StrategyRdAccountRun,
} from "../../../services/strategyRdAccountApi";
import { formatTradeDateTimeForDisplay } from "../../../utils/displayDateTime";
import { formatStrategyRdMoney } from "../../../utils/strategyRd";
import {
  accountSettingsFieldDescriptionClassName,
  accountSettingsNestedCardClassName,
} from "../Profile/accountSettingsCardShell";
import { settingCardTitleClassName } from "../Profile/components/SettingCard";
import { statsCardHeadlineClassName } from "../Statistics/components/statisticsCardHeadline";
import {
  strategyRdBrandActionButtonClassName,
  strategyRdMetaClassName,
} from "./strategyRdCardShell";
import {
  StrategyRdBadge,
  StrategyRdInfo,
  StrategyRdLabCard,
  StrategyRdStat,
  StrategyRdStatGrid,
} from "./strategyRdLabUi";

/** Settings fields: one column on phones, 2 then 4 across, all 8 on one row on a wide card. */
const ACCOUNT_SETTINGS_GRID_CLASS = cn(
  "grid grid-cols-1 items-end gap-x-4 gap-y-5",
  "@md/accountsettings:grid-cols-2 @3xl/accountsettings:grid-cols-4",
  "@7xl/accountsettings:grid-cols-[minmax(0,1.6fr)_minmax(0,1.2fr)_repeat(5,minmax(0,1fr))_auto]"
);
const ACCOUNT_FIELD_CLASS = "min-w-0 gap-2";
/** The app's settings field label (Profile / broker cards): Tropical Surf, sentence case. */
const ACCOUNT_FIELD_LABEL_CLASS = cn(BROKER_ALERT_TITLE_CLASS, "whitespace-nowrap");
const ACCOUNT_INPUT_CLASS = cn(
  "h-8 w-full rounded-[6px] font-[family-name:var(--font-primary)] text-sm font-normal",
  "text-gray-500 placeholder:text-gray-400 dark:text-white dark:placeholder:text-white/60"
);

/** "Close all at" choices: Off, then every 30 minutes (HH:MM, New York time; "" = off). */
const FLAT_AT_OPTIONS: TradeFilterSelectOption[] = [
  { value: "", label: "Off" },
  ...Array.from({ length: 48 }, (_, i) => {
    const hhmm = `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`;
    return { value: hhmm, label: hhmm };
  }),
];

// Brand kit (src/Branding/brand-guidelines.md): Glacial green / red for good / bad, grey for neutral.
const PASS_COLOR = "var(--brand-glacial-green)";
const FAIL_COLOR = "var(--brand-glacial-red)";
const STALL_COLOR = "#d3d3d3"; // Soft Glacial Grey
const POS_COLOR = "var(--brand-glacial-green)";
const NEG_COLOR = "var(--brand-glacial-red)";
/** Headline numbers: IBM Plex Sans (brand secondary font) for figures on cards. */

/** Shown where a figure is missing (never an em dash). */
const NONE = "N/A";

const share = (v: number | null | undefined): string => (v == null ? NONE : `${Math.round(v * 100)}%`);
const pct = (v: number | null | undefined): string =>
  v == null ? NONE : `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;
const days = (v: number | null | undefined): string => (v == null ? NONE : `${Math.round(v)}`);
/** "42 days", "5 months", "3.6 years". */
const duration = (d: number | null | undefined): string => {
  if (d == null) return NONE;
  if (d < 60) return `${Math.round(d)} days`;
  if (d < 730) return `${Math.round(d / 30.4)} months`;
  return `${(d / 365).toFixed(1)} years`;
};

/**
 * How a run that didn't finish was going, projected forward: "+0.11% a day → +10% in about
 * 3 months at this pace", or that it never gets there because it lost money.
 */
export function strategyRdChallengePace(returnPct: number | null, days: number | null, targetPct: number | null): string | null {
  if (returnPct == null || days == null || days <= 0 || targetPct == null) return null;
  if (returnPct <= 0) return `${returnPct < 0 ? "Lost money" : "Made nothing"}. At this pace it never reaches +${targetPct}%.`;
  if (returnPct >= targetPct) return null;
  const perDay = returnPct / days;
  // Small daily gains keep two decimals ("+0.01%"), not a misleading "+0.0%".
  const daily = `+${perDay.toFixed(perDay < 0.01 ? 3 : perDay < 0.1 ? 2 : 1)}%`;
  return `About ${daily} a day → +${targetPct}% in about ${duration(targetPct / perDay)} at this pace.`;
}

/** One plain sentence on why the challenge passes, fails or stalls — and what would change it. */
function challengeVerdict(
  c: NonNullable<StrategyRdAccountBalanceResult["challenge"]>,
  horizon: number,
  outOfData = false
): string {
  const target = c.targetPct != null ? `+${c.targetPct}%` : "the target";
  if (c.passRate >= 0.5) {
    return `Most start dates pass, typically in ${duration(c.daysToPass.median)}.`;
  }
  if (c.failRate > c.passRate && c.failRate >= 0.2) {
    return "More runs break a limit than pass: lower the risk per trade or keep the never-breach guard on.";
  }
  const got = c.stalledReturnPct.median;
  const trades = c.tradesPerRun;
  if (got != null && got < 0) {
    return `A typical run lost money (${pct(got)} in ${duration(horizon)}${trades != null ? `, about ${Math.round(trades)} trades` : ""}). At this pace it never reaches ${target}. Over these weeks the strategy itself lost; improve it in the backtest before testing a challenge.`;
  }
  const pace = strategyRdChallengePace(got, horizon, c.targetPct);
  return outOfData
    ? `In the ${duration(horizon)} of data each run had, a typical one made ${pct(got)}${trades != null ? ` from about ${Math.round(trades)} trades` : ""}. ${pace ?? ""} A longer backtest shows whether it actually gets there.`
    : `In the ${duration(horizon)} each run had, a typical one made ${pct(got)}${trades != null ? ` from about ${Math.round(trades)} trades` : ""}, far from ${target}, so it ran out of time. ${pace ?? ""}`;
}

/** "1000, 5k, 10000" → [1000, 5000, 10000] (at most 6, each > 0). */
export function parseStrategyRdBalances(text: string): number[] {
  return text
    .split(/[,\s]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean)
    .map((t) => (t.endsWith("k") ? parseFloat(t) * 1000 : parseFloat(t)))
    .filter((n) => Number.isFinite(n) && n > 0)
    .slice(0, 6);
}

const SKIP_LABELS: Record<string, string> = {
  guard: "guard (would risk a breach)",
  below_min_lot: "below the broker minimum lot",
  target_reached: "target reached",
  kill_switch: "kill switch",
  daily_stop: "daily stop",
  after_flat_time: "after the flat-at time",
  open_risk_cap: "open-risk cap",
};

const REASON_LABELS: Record<string, string> = {
  daily_loss: "daily loss limit",
  max_loss: "max loss",
  time_limit: "time limit",
  best_day: "best-day rule",
};

const STATUS_LABELS: Record<string, string> = {
  // This run has no time limit: it only stops short of the target when the backtest's data ends.
  stalled: "ran out of data",
  killed: "kill switch hit",
  completed: "ran to the end",
  failed: "broke a rule",
};

type Rec = Record<string, unknown>;
const rec = (v: unknown): Rec => (v && typeof v === "object" && !Array.isArray(v) ? (v as Rec) : {});
const n = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** The preset's rules in plain words, one line per phase plus the risk policy. */
export function describeStrategyRdAccountRules(rules: Rec | null | undefined): { phase: string; lines: string[] }[] {
  const r = rec(rules);
  const tz = typeof r.day_timezone === "string" && r.day_timezone !== "UTC" ? ` (the day resets at midnight ${String(r.day_timezone).split("/").pop()?.replace("_", " ")} time)` : "";
  const phases = Array.isArray(r.phases) ? (r.phases as unknown[]).map(rec) : [];
  const out = phases.map((p) => {
    const lines: string[] = [];
    const target = n(p.target_pct);
    if (target != null) lines.push(`Reach +${target}% profit.`);
    const dl = rec(p.daily_loss);
    if (n(dl.pct) != null) lines.push(`Never lose more than ${n(dl.pct)}% in a day${tz}, counting open trades.`);
    const ml = rec(p.max_loss);
    if (n(ml.pct) != null) {
      const kind = String(ml.kind || "static");
      lines.push(
        kind === "static"
          ? `Never fall more than ${n(ml.pct)}% below the starting balance.`
          : kind === "trailing_eod"
            ? `Never fall ${n(ml.pct)}% below your best end-of-day balance${ml.lock_at_initial ? " (the floor stops at the starting balance)" : ""}.`
            : `Never fall ${n(ml.pct)}% below your highest equity${ml.lock_at_initial ? " (the floor stops at the starting balance)" : ""}.`
      );
    }
    if (n(p.min_trading_days)) lines.push(`Trade on at least ${n(p.min_trading_days)} days.`);
    if (n(p.max_days)) lines.push(`Within ${n(p.max_days)} days.`);
    if (n(p.best_day_share) != null) lines.push(`Your best day can be at most ${Math.round((n(p.best_day_share) as number) * 100)}% of the total profit.`);
    const po = rec(p.payout);
    if (n(po.every_days) != null) lines.push(`Payout every ${n(po.every_days)} days: you keep ${n(po.split_pct) ?? 80}% of the profit, then the account restarts from its starting balance.`);
    if (!lines.length) lines.push("No firm limits, only your own rules below.");
    return { phase: String(p.name || "Account"), lines };
  });
  const risk = rec(r.risk);
  const own: string[] = [];
  if (n(risk.guard) != null) own.push(`Never-breach guard: a trade is only taken if all open stops plus its own stop stay inside ${Math.round((n(risk.guard) as number) * 100)}% of the daily limit and above the floor.`);
  if (n(risk.floor_fraction) != null) own.push(`Each trade risks at most 1/${Math.round(1 / (n(risk.floor_fraction) as number))} of the room above the floor.`);
  if (n(risk.kill_switch_pct) != null) own.push(`Stop trading for good after a ${n(risk.kill_switch_pct)}% drop from the peak.`);
  if (n(risk.open_risk_cap_pct) != null) own.push(`All open stops together risk at most ${n(risk.open_risk_cap_pct)}% of the balance.`);
  if (n(risk.daily_stop_pct) != null) own.push(`No new trades for the rest of the day after losing ${n(risk.daily_stop_pct)}% of the day's starting balance.`);
  const flat = rec(risk.flat_at);
  if (typeof flat.at === "string") own.push(`Close every open trade at ${flat.at} New York${flat.days === "friday" ? " on Fridays (no weekend holding)" : " every day"}.`);
  if (risk.size_on === "initial") own.push("Size every trade on the starting balance (the same size all challenge).");
  if (own.length) out.push({ phase: "Your rules", lines: own });
  return out;
}

export type StrategyRdAccountPanelProps = {
  hypothesisId: string | null;
  /** Opens TES with a question about this result. */
  onAskTes?: (question: string) => void;
  /** The backtest on screen (a new one clears the old result). */
  appliedAt: string | null;
  startingEquity?: number | null;
  timeZone: string;
  currency?: string | null;
};

/**
 * Account test: the backtest's trades through a personal account's risk rules or a prop-firm
 * challenge, from every week start and for each balance — the distribution, never one start date.
 */
export function StrategyRdAccountPanel({
  hypothesisId,
  appliedAt,
  startingEquity = null,
  timeZone,
  currency = null,
  onAskTes,
}: StrategyRdAccountPanelProps): JSX.Element | null {
  const [presets, setPresets] = useState<StrategyRdAccountPreset[]>([]);
  const [presetId, setPresetId] = useState("personal");
  const [balances, setBalances] = useState("");
  const [guard, setGuard] = useState(true);
  const [killSwitch, setKillSwitch] = useState("50");
  // Extra own rules (blank = off): stop for the day after losing X%, cap on all open stops'
  // risk, and close everything at a time (every day, or Fridays only).
  const [dailyStop, setDailyStop] = useState("");
  const [openRiskCap, setOpenRiskCap] = useState("");
  const [flatAt, setFlatAt] = useState("");
  const [flatDays, setFlatDays] = useState<"daily" | "friday">("daily");
  const [realistic, setRealistic] = useState(true);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<StrategyRdAccountResult | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void listStrategyRdAccountPresets(controller.signal)
      .then((p) => setPresets(p))
      .catch(() => setPresets([]));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    setResult(null);
    setError(null);
    abortRef.current?.abort();
  }, [hypothesisId, appliedAt]);
  useEffect(() => () => abortRef.current?.abort(), []);

  const preset = useMemo(() => presets.find((p) => p.id === presetId) ?? null, [presets, presetId]);
  const challenge = preset?.isChallenge ?? false;
  const defaultBalance = challenge ? preset?.initialBalance ?? 10_000 : startingEquity ?? preset?.initialBalance ?? 10_000;

  const extraRisk = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    const d = parseFloat(dailyStop);
    if (Number.isFinite(d) && d > 0) out.daily_stop_pct = d;
    const c = parseFloat(openRiskCap);
    if (Number.isFinite(c) && c > 0) out.open_risk_cap_pct = c;
    if (/^\d{2}:\d{2}$/.test(flatAt)) out.flat_at = { at: flatAt, tz: "America/New_York", days: flatDays };
    return out;
  };

  if (!hypothesisId) return null;

  const run = (): void => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const list = parseStrategyRdBalances(balances);
    const bal = list.length > 0 ? list : [defaultBalance];
    const risk: Record<string, unknown> = {};
    if (challenge) risk.guard = guard ? 0.8 : null;
    else {
      const k = parseFloat(killSwitch);
      risk.kill_switch_pct = Number.isFinite(k) && k > 0 ? k : null;
    }
    Object.assign(risk, extraRisk());
    setRunning(true);
    setError(null);
    setProgress("Queued…");
    void runStrategyRdAccountTest(
      hypothesisId,
      {
        rules: { preset: presetId, initial_balance: bal[0], risk },
        balances: bal,
        costs: realistic ? "realistic" : "as_set",
      },
      controller.signal,
      (p) => setProgress(p.message)
    )
      .then((r) => {
        if (!controller.signal.aborted) setResult(r);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : "Could not run the account test.");
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setRunning(false);
          setProgress(null);
        }
      });
  };

  const ask =
    result && !running && onAskTes ? (
      <Button
        type="button"
        variant="outline-brand"
        size="sm"
        data-font="primary"
        className={strategyRdBrandActionButtonClassName}
        onClick={() =>
          onAskTes(
            challenge
              ? `Walk me through this ${result.rulesName} account test: what decides passing, why runs stall or fail, and what to change.`
              : `Walk me through this account test: what to expect from a random start date and how to cut the risk of a big drop.`
          )
        }
      >
        <MessageCircleQuestion className="size-4" aria-hidden />
        <span className="normal-case tracking-normal">Ask TES</span>
      </Button>
    ) : null;

  const rulesSummary = preset
    ? describeStrategyRdAccountRules({
        ...preset.rules,
        risk: {
          ...rec(preset.rules.risk),
          ...(challenge ? { guard: guard ? 0.8 : null } : { kill_switch_pct: parseFloat(killSwitch) || null }),
          ...extraRisk(),
        },
      })
    : [];

  return (
    <StrategyRdLabCard
      title={
        <span className="flex items-center gap-1.5">
          Account test
          <StrategyRdInfo title="What the account test does">
            <p>
              A backtest says how the <strong>strategy</strong> did. The account test says how an <strong>account</strong>{" "}
              trading it would have done: the same trades, run through real account rules such as daily loss limits,
              maximum loss, profit targets and margin.
            </p>
            <p>
              Instead of one start date, it starts the account on <strong>every week</strong> of the data. That shows
              how much depends on when you begin: the share of starts that passed or blew up, typical time to pass,
              and the worst drop you should expect.
            </p>
          </StrategyRdInfo>
        </span>
      }
      description="This backtest's trades run through an account's risk rules (your own, or a prop-firm challenge), starting on every week of the data, for each balance. Limits are checked on open trades inside every candle."
      actions={ask}
      data-strategy-rd-account=""
    >
      <div className={cn(accountSettingsNestedCardClassName, "@container/accountsettings gap-5 p-5")}>
        <div className={ACCOUNT_SETTINGS_GRID_CLASS}>
          <Field className={ACCOUNT_FIELD_CLASS}>
            <FieldLabel htmlFor="strategy-rd-account-preset" className={cn(ACCOUNT_FIELD_LABEL_CLASS, "items-center gap-1")}>
              Account
              <StrategyRdInfo title="Which account">
                <p>
                  <strong>Personal account</strong>: your own money with your own safety rules, such as a kill switch
                  (stop trading after the account falls this far from its peak) and the realistic costs below.
                </p>
                <p>
                  <strong>Prop-firm challenges</strong> (FTMO, The5ers, Topstep…): their exact rules, including the
                  profit target, daily loss limit, maximum loss and minimum trading days. The test tells you how often
                  this strategy would pass, how long it takes, and what it would pay you once funded.
                </p>
              </StrategyRdInfo>
            </FieldLabel>
            <FieldSelectFullWidth
              id="strategy-rd-account-preset"
              value={presetId}
              disabled={running}
              onValueChange={setPresetId}
              options={(presets.length ? presets : [{ id: "personal", name: "Personal account" } as StrategyRdAccountPreset]).map(
                (p) => ({ value: p.id, label: p.isChallenge ? `${p.name} · challenge` : p.name })
              )}
            />
          </Field>
          <Field className={ACCOUNT_FIELD_CLASS}>
            <FieldLabel htmlFor="strategy-rd-account-balances" className={ACCOUNT_FIELD_LABEL_CLASS}>
              Starting balances
            </FieldLabel>
            <Input
              id="strategy-rd-account-balances"
              className={ACCOUNT_INPUT_CLASS}
              placeholder={String(defaultBalance)}
              value={balances}
              onChange={(e) => setBalances(e.target.value)}
              disabled={running}
              title="Up to 6, e.g. 1000, 5k, 10k"
            />
          </Field>
          {challenge ? (
            <Field className={ACCOUNT_FIELD_CLASS} title="Stop trading for the day before a daily limit can be hit">
              <FieldLabel htmlFor="strategy-rd-account-guard" className={ACCOUNT_FIELD_LABEL_CLASS}>
                Never-breach guard
              </FieldLabel>
              <div className="flex h-8 items-center">
                <Switch
                  id="strategy-rd-account-guard"
                  checked={guard}
                  onCheckedChange={setGuard}
                  disabled={running}
                  aria-label="Never-breach guard"
                />
              </div>
            </Field>
          ) : (
            <Field className={ACCOUNT_FIELD_CLASS} title="Stop trading for good after the account falls this % from its peak">
              <FieldLabel htmlFor="strategy-rd-account-kill-switch" className={ACCOUNT_FIELD_LABEL_CLASS}>
                Kill switch %
              </FieldLabel>
              <Input
                id="strategy-rd-account-kill-switch"
                className={ACCOUNT_INPUT_CLASS}
                type="number"
                min={1}
                max={100}
                value={killSwitch}
                onChange={(e) => setKillSwitch(e.target.value)}
                disabled={running}
              />
            </Field>
          )}
          <Field
            className={ACCOUNT_FIELD_CLASS}
            title="No new trades for the rest of the day after losing this % of the day's starting balance. Blank = off."
          >
            <FieldLabel htmlFor="strategy-rd-account-daily-stop" className={ACCOUNT_FIELD_LABEL_CLASS}>
              Daily stop %
            </FieldLabel>
            <Input
              id="strategy-rd-account-daily-stop"
              className={ACCOUNT_INPUT_CLASS}
              type="number"
              min={0.1}
              max={100}
              step={0.1}
              placeholder="Off"
              value={dailyStop}
              onChange={(e) => setDailyStop(e.target.value)}
              disabled={running}
            />
          </Field>
          <Field
            className={ACCOUNT_FIELD_CLASS}
            title="All open trades' stops together may risk at most this % of the balance; new trades shrink to fit. Blank = off."
          >
            <FieldLabel htmlFor="strategy-rd-account-open-risk" className={ACCOUNT_FIELD_LABEL_CLASS}>
              Open risk cap %
            </FieldLabel>
            <Input
              id="strategy-rd-account-open-risk"
              className={ACCOUNT_INPUT_CLASS}
              type="number"
              min={0.1}
              max={100}
              step={0.1}
              placeholder="Off"
              value={openRiskCap}
              onChange={(e) => setOpenRiskCap(e.target.value)}
              disabled={running}
            />
          </Field>
          <Field className={ACCOUNT_FIELD_CLASS} title="Close every open trade at this New York time. Off = never.">
            <FieldLabel htmlFor="strategy-rd-account-flat-at" className={ACCOUNT_FIELD_LABEL_CLASS}>
              Close all at (NY)
            </FieldLabel>
            <FieldSelectFullWidth
              id="strategy-rd-account-flat-at"
              value={flatAt}
              disabled={running}
              onValueChange={setFlatAt}
              options={FLAT_AT_OPTIONS}
            />
          </Field>
          <Field className={ACCOUNT_FIELD_CLASS} title="Which days the close-all time applies to">
            <FieldLabel htmlFor="strategy-rd-account-flat-days" className={ACCOUNT_FIELD_LABEL_CLASS}>
              Close on
            </FieldLabel>
            <FieldSelectFullWidth
              id="strategy-rd-account-flat-days"
              value={flatDays}
              disabled={running || !flatAt}
              onValueChange={(v) => setFlatDays(v === "friday" ? "friday" : "daily")}
              options={[
                { value: "daily", label: "Every day" },
                { value: "friday", label: "Fridays" },
              ]}
            />
          </Field>
          <Field
            className={ACCOUNT_FIELD_CLASS}
            title="Typical spread and overnight swap for this instrument, where the backtest has none"
          >
            <FieldLabel htmlFor="strategy-rd-account-realistic" className={ACCOUNT_FIELD_LABEL_CLASS}>
              Realistic costs
            </FieldLabel>
            <div className="flex h-8 items-center">
              <Switch
                id="strategy-rd-account-realistic"
                checked={realistic}
                onCheckedChange={setRealistic}
                disabled={running}
                aria-label="Realistic costs"
              />
            </div>
          </Field>
        </div>

        <div className="flex flex-col gap-3 border-t border-solid border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className={cn(accountSettingsFieldDescriptionClassName, "m-0")}>
            Leave a rule blank or Off to skip it. Up to 6 balances, e.g. 1000, 5k, 10k.
          </p>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="shrink-0 self-start rounded-[6px] px-4 sm:self-auto"
            onClick={run}
            disabled={running || !appliedAt}
          >
            {running ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            <span className="normal-case tracking-normal">{running ? progress || "Running" : "Run account test"}</span>
          </Button>
        </div>
      </div>

      {preset ? (
        <div className="@container/accountrules" data-strategy-rd-account-rules="">
          <div className="grid grid-cols-1 gap-4 @2xl/accountrules:grid-cols-2">
            {rulesSummary.map((block) => (
              <div key={block.phase} className={accountSettingsNestedCardClassName}>
                <div className="border-b border-solid border-border px-5 py-3">
                  <h4 className={cn(statsCardHeadlineClassName, "m-0")}>{block.phase}</h4>
                </div>
                <ul className="m-0 flex list-none flex-col divide-y divide-border p-0">
                  {block.lines.map((line) => (
                    <li key={line} className="flex items-start gap-3 px-5 py-3">
                      <span className="flex h-5 shrink-0 items-center" aria-hidden>
                        <Check className="size-3.5 text-[color:var(--brand-tropical-surf)]" />
                      </span>
                      <span className={accountSettingsFieldDescriptionClassName}>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {error ? (
        <p className={strategyRdMetaClassName} style={{ color: NEG_COLOR }}>
          {error}
        </p>
      ) : null}

      {result && !running ? (
        <div className="flex flex-col gap-4" data-strategy-rd-account-result="">
          <p className={cn(accountSettingsFieldDescriptionClassName, "m-0 text-xs")}>
            {result.rulesName} · {result.starts} start dates ({result.every}ly) · {result.trades} trades
            {result.horizonDays ? ` · each run ${result.horizonDays} days` : ""}
            {result.minLot ? ` · minimum lot ${result.minLot} units` : ""}
          </p>
          {result.horizonShortened ? (
            // The research disclaimer's row layout in its warning tone: icon, brand title, grey body.
            <Alert
              variant="warning"
              data-strategy-rd-account-short-data=""
              className="flex w-full items-start gap-3 rounded-lg px-4 py-3 font-[family-name:var(--font-primary)]"
            >
              <span aria-hidden className="flex h-5 w-4 shrink-0 items-center justify-center">
                <CalendarRange className="size-4" />
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p className="m-0 font-[family-name:var(--font-primary)] text-sm font-medium leading-5 text-[color:var(--brand-tropical-surf)]">
                  Each run ran out of data after {result.horizonDays} days. The backtest has {Math.round(result.dataDays ?? 0)} days.
                </p>
                <AlertDescription className="font-[family-name:var(--font-primary)] text-sm font-normal leading-5 !text-gray-500 dark:!text-white">
                  {challenge
                    ? `A challenge usually gets ${result.fullHorizonDays ?? 180} days here, and many pass only after months. `
                    : `An account usually runs ${result.fullHorizonDays ?? 365} days here. `}
                  To test {challenge ? "it" : "the account"} properly, create a longer backtest (up to 180 days on 1m to 15m
                  charts, years on 1h and above) and run the account test on it. Until then, the pace under each run
                  shows where it was heading.
                </AlertDescription>
              </div>
            </Alert>
          ) : result.note ? (
            <p className={strategyRdMetaClassName}>{result.note}</p>
          ) : null}
          {result.byBalance.map((b) => (
            <BalanceCard
              key={b.balance}
              b={b}
              horizonDays={result.horizonDays}
              timeZone={timeZone}
              currency={currency}
              killSwitchPct={challenge ? null : parseFloat(killSwitch) || null}
              outOfData={result.horizonShortened}
              phases={preset ? describeStrategyRdAccountRules(preset.rules).filter((x) => x.phase !== "Your rules") : []}
            />
          ))}
          {result.firstRun.phases.length > 0 ? (
            <div className={cn(accountSettingsNestedCardClassName, "overflow-hidden")}>
              <div className="flex items-center gap-1.5 !border-0 !border-b !border-solid !border-border px-5 py-3">
                <h4 className="m-0 font-[family-name:var(--font-primary)] text-base font-medium text-[color:var(--brand-tropical-surf)]">
                  One run from the first trade
                </h4>
                <StrategyRdInfo title="One run from the first trade">
                  <p>
                    The runs above each start on a different week and last the same number of days. This one starts
                    at the backtest&apos;s very first trade and keeps going for every day of data, so you see how a
                    single attempt would have played out, phase by phase.
                  </p>
                </StrategyRdInfo>
              </div>
              <div className="flex flex-col divide-y divide-border">
                {result.firstRun.phases.map((p, i) => (
                  <div
                    key={`${p.name}:${i}`}
                    className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-5 py-3 font-[family-name:var(--font-primary)] text-sm"
                  >
                    <span className="font-medium text-[color:var(--brand-tropical-surf)]">{p.name}</span>
                    <StrategyRdBadge tone={p.status === "passed" || p.status === "funded" ? "good" : p.status === "failed" ? "bad" : "neutral"}>
                      {STATUS_LABELS[p.status] ?? p.status}
                      {p.reason ? ` · ${REASON_LABELS[p.reason] ?? p.reason}` : ""}
                    </StrategyRdBadge>
                    <span className="font-[family-name:var(--font-secondary)] text-xs text-gray-500 dark:text-white">
                      {duration(p.days)} · {p.trades ?? 0} trades · {pct(p.returnPct)} · worst drop{" "}
                      {pct(p.maxDrawdownPct == null ? null : -p.maxDrawdownPct)}
                      {p.paid != null ? ` · paid to you ${formatStrategyRdMoney(p.paid, currency)}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </StrategyRdLabCard>
  );
}

function RateBar({ pass, fail, stalled }: { pass: number; fail: number; stalled: number }): JSX.Element {
  const seg = (v: number, color: string, label: string) =>
    v > 0 ? <div style={{ width: `${v * 100}%`, background: color }} title={`${label} ${share(v)}`} /> : null;
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
      {seg(pass, PASS_COLOR, "Passed")}
      {seg(fail, FAIL_COLOR, "Broke a rule")}
      {seg(stalled, STALL_COLOR, "Ran out of time")}
    </div>
  );
}

const RUN_REASON: Record<string, string> = {
  daily_loss: "broke the daily loss limit",
  max_loss: "broke the max loss limit",
  time_limit: "hit the time limit",
  best_day: "broke the best-day rule",
  kill_switch: "kill switch",
};

/** What happened to one start date, in words. */
function runOutcome(
  r: StrategyRdAccountRun,
  challenge: boolean,
  targetPct: number | null,
  outOfData = false
): { text: string; tone: "good" | "bad" | "neutral"; pace?: string | null } {
  const day = r.days != null ? ` on day ${Math.max(1, Math.ceil(r.days))}` : "";
  if (challenge) {
    if (r.passed) return { text: `Passed in ${days(r.days)} days${r.paid > 0 ? " · funded, paid out" : ""}`, tone: "good" };
    if (r.status === "failed") return { text: `Failed${day}: ${RUN_REASON[r.reason ?? ""] ?? r.reason ?? "a limit"}`, tone: "bad" };
    return {
      text: outOfData
        ? `Ran out of data after ${days(r.days)} days at ${pct(r.returnPct)}${targetPct != null ? ` (needs +${targetPct}%)` : ""}`
        : `Ran out of time at ${pct(r.returnPct)}${targetPct != null ? ` (needs +${targetPct}%)` : ""}`,
      tone: "neutral",
      pace: strategyRdChallengePace(r.returnPct, r.days, targetPct),
    };
  }
  if (r.status === "killed") {
    const kd = r.killedDay != null ? ` on day ${Math.max(1, Math.ceil(r.killedDay))}` : "";
    return { text: `Kill switch stopped trading${kd} at ${pct(r.returnPct)}`, tone: "bad" };
  }
  return { text: `Ended ${pct(r.returnPct)}`, tone: (r.returnPct ?? 0) >= 0 ? "good" : "bad" };
}

const ACCOUNT_RUN_PAGE_SIZE = 20;

/**
 * One row per start date — what the summary numbers are made of. Personal: a bar from zero to the
 * result (green up, red down). Challenge: progress toward the profit target.
 */
function RunRows({
  runs,
  challenge,
  targetPct,
  timeZone,
  outOfData = false,
}: {
  runs: StrategyRdAccountRun[];
  challenge: boolean;
  targetPct: number | null;
  timeZone: string;
  outOfData?: boolean;
}): JSX.Element | null {
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: ACCOUNT_RUN_PAGE_SIZE });
  if (!runs.length) return null;
  const span = Math.max(1, ...runs.map((r) => Math.abs(r.returnPct ?? 0)), challenge && targetPct ? targetPct : 0);
  // Challenge bars: zero in the middle, losses to the left (to the worst run), gains to the right up to the target.
  const lossSpan = Math.max(1, ...runs.map((r) => Math.max(0, -(r.returnPct ?? 0))));
  const columns: ColumnDef<StrategyRdAccountRun, unknown>[] = [
    {
      id: "start",
      accessorFn: (r) => (r.start ? Date.parse(r.start) : null),
      header: ({ column }) => <DataTableColumnHeader column={column} title="Start" />,
      cell: ({ row }) => (
        <TradeLogSortableCell>
          {row.original.start ? formatTradeDateTimeForDisplay(row.original.start, timeZone) : NONE}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "bar",
      enableSorting: false,
      // The bar scale: worst loss on the left, best gain (or the target) on the right.
      header: () => (
        <span className="flex min-w-[9rem] justify-between gap-2 tabular-nums">
          <span style={{ color: NEG_COLOR }}>{challenge ? pct(-lossSpan) : pct(-span)}</span>
          <span>0</span>
          <span style={{ color: challenge ? PASS_COLOR : POS_COLOR }}>
            {challenge ? `+${targetPct ?? "?"}% target` : pct(span)}
          </span>
        </span>
      ),
      cell: ({ row }) => {
        const r = row.original;
        const ret = r.returnPct ?? 0;
        return challenge ? (
          <div className="relative h-2 min-w-[9rem] rounded-full bg-muted" title={`${pct(ret)}, target +${targetPct ?? "?"}%`}>
            {/* Zero, and the target at the right end. */}
            <div className="absolute -inset-y-1 left-1/2 w-px bg-foreground/40" />
            <div className="absolute -inset-y-1 right-0 w-px" style={{ background: PASS_COLOR }} />
            <div
              className="absolute inset-y-0 rounded-full"
              style={
                ret >= 0
                  ? {
                      left: "50%",
                      width: `${Math.min(50, (ret / Math.max(0.01, targetPct ?? span)) * 50)}%`,
                      background: r.passed ? PASS_COLOR : "var(--brand-tropical-surf)",
                    }
                  : {
                      left: `${50 - Math.min(50, (-ret / lossSpan) * 50)}%`,
                      width: `${Math.min(50, (-ret / lossSpan) * 50)}%`,
                      background: NEG_COLOR,
                    }
              }
            />
          </div>
        ) : (
          <div className="relative h-2 min-w-[9rem]" title={pct(ret)}>
            <div className="absolute inset-y-0 left-1/2 w-px bg-border" />
            <div
              className="absolute inset-y-0 rounded-full"
              style={{
                left: ret >= 0 ? "50%" : `${50 - (Math.abs(ret) / span) * 50}%`,
                width: `${(Math.abs(ret) / span) * 50}%`,
                background: ret >= 0 ? POS_COLOR : NEG_COLOR,
              }}
            />
          </div>
        );
      },
    },
    {
      id: "result",
      accessorFn: (r) => r.returnPct ?? null,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Result" />,
      cell: ({ row }) => {
        const out = runOutcome(row.original, challenge, targetPct, outOfData);
        const color = out.tone === "good" ? POS_COLOR : out.tone === "bad" ? NEG_COLOR : STALL_COLOR;
        return (
          <TradeLogSortableCell className="font-medium">
            <span className="flex flex-col gap-0.5">
              <span style={{ color }}>{out.text}</span>
              {out.pace ? (
                <span className="font-normal text-muted-foreground" data-strategy-rd-account-pace="">
                  {out.pace}
                </span>
              ) : null}
            </span>
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "trades",
      accessorFn: (r) => r.trades ?? 0,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Trades" />,
      cell: ({ row }) => <TradeLogSortableCell className="font-medium">{row.original.trades ?? 0}</TradeLogSortableCell>,
    },
    {
      id: "worstDrop",
      accessorFn: (r) => r.maxDrawdownPct ?? null,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Worst drop" />,
      cell: ({ row }) => (
        <TradeLogSortableCell className="font-medium">
          {pct(row.original.maxDrawdownPct == null ? null : -row.original.maxDrawdownPct)}
        </TradeLogSortableCell>
      ),
    },
  ];
  return (
    <div className="flex flex-col gap-2" data-strategy-rd-account-runs="">
      <span className={statsCardHeadlineClassName}>Each start date</span>
      {/* The app's data table, as Trades and the Trade Log: sortable, paged. */}
      <DataTable
        columns={columns}
        data={runs}
        getRowId={(r) => `${r.start}`}
        pagination={pagination}
        onPaginationChange={setPagination}
        showPagination={runs.length > ACCOUNT_RUN_PAGE_SIZE}
        tableClassName="dark:!bg-sidebar"
        emptyMessage="No start dates."
      />
    </div>
  );
}

function BalanceCard({
  b,
  horizonDays,
  timeZone,
  currency,
  killSwitchPct,
  phases,
  outOfData = false,
}: {
  b: StrategyRdAccountBalanceResult;
  horizonDays: number;
  timeZone: string;
  currency: string | null;
  killSwitchPct: number | null;
  phases: { phase: string; lines: string[] }[];
  /** Runs stopped because the backtest ended, not because a rule or time limit did. */
  outOfData?: boolean;
}): JSX.Element {
  const outWord = outOfData ? "ran out of data" : "ran out of time";
  const money = (v: number | null | undefined) => (v == null ? NONE : formatStrategyRdMoney(v, currency) ?? NONE);
  const skipped = Object.entries(b.skippedPerRun ?? {}).filter(([, v]) => v >= 0.5);
  const ch = b.challenge;
  const pe = b.personal;
  const fu = b.funded;
  const runs = b.runList ?? [];
  const nRuns = runs.length || b.runs;
  const count = (f: (r: StrategyRdAccountRun) => boolean) => runs.filter(f).length;
  const killedShare = b.statusShare.killed ?? 0;

  const summary = ch
    ? (() => {
        const passed = count((r) => r.passed);
        const failed = count((r) => !r.passed && r.status === "failed");
        const out = nRuns - passed - failed;
        const best = Math.max(...runs.filter((r) => !r.passed && r.status !== "failed").map((r) => r.returnPct ?? -Infinity));
        return (
          `We started the challenge with ${money(b.balance)} on ${nRuns} different week${nRuns === 1 ? "" : "s"}, ` +
          `${horizonDays} days each. ${passed} passed, ${failed} broke a rule, ${out} ${outWord}` +
          (out > 0 && Number.isFinite(best) ? ` (the best of those reached ${pct(best)} of the +${ch.targetPct ?? "?"}% needed).` : ".")
        );
      })()
    : pe
      ? `We started a ${money(b.balance)} account on ${nRuns} different week${nRuns === 1 ? "" : "s"} and traded ${horizonDays} days each. ` +
        `Typical result: ${pct(pe.returnPct.median)}. ${count((r) => (r.returnPct ?? 0) < 0)} of ${nRuns} ended below where they began.` +
        (killSwitchPct ? ` Your ${killSwitchPct}% kill switch stopped trading in ${count((r) => r.status === "killed")} of ${nRuns}.` : "")
      : null;

  return (
    <div
      className={cn(accountSettingsNestedCardClassName, "gap-4 p-5")}
      data-strategy-rd-account-balance={b.balance}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className={cn(settingCardTitleClassName, "m-0")}>{money(b.balance)} account</h4>
        <span className={cn(statsCardHeadlineClassName, "tabular-nums")}>{b.runs} runs</span>
      </div>
      {ch && phases.length > 0 ? (
        <ol className="grid gap-2 sm:grid-cols-2" data-strategy-rd-account-steps="">
          {phases.map((ph, i) => (
            <li key={ph.phase} className="flex flex-col gap-1 rounded-[6px] border border-solid border-border bg-background px-4 py-3">
              <span className={BROKER_ALERT_TITLE_CLASS}>
                Step {i + 1} · {ph.phase}
              </span>
              <p className={cn(accountSettingsFieldDescriptionClassName, "m-0 text-xs")}>
                {/* Each step's goal; its limits are listed above the Run button. */}
                {ph.lines.filter((l) => /^(Reach|Payout|Trade on at least|Within)/.test(l)).join(" ") ||
                  "Keep trading inside the limits above."}
              </p>
            </li>
          ))}
        </ol>
      ) : null}
      {summary ? (
        <p className={cn(accountSettingsFieldDescriptionClassName, "m-0 leading-relaxed")} data-strategy-rd-account-summary="">
          {summary}
        </p>
      ) : null}
      <RunRows runs={runs} challenge={Boolean(ch)} targetPct={ch?.targetPct ?? null} timeZone={timeZone} outOfData={outOfData} />
      {ch ? (
        <>
          <StrategyRdStatGrid columns={fu && fu.accounts > 0 ? 4 : 3} variant="cards" className="items-start">
            <StrategyRdStat label="Passed" value={share(ch.passRate)} valueColor={ch.passRate > 0 ? PASS_COLOR : undefined} unit="of start dates">
              <div className="my-1.5">
                <RateBar pass={ch.passRate} fail={ch.failRate} stalled={ch.stalledRate} />
              </div>
              <span style={{ color: FAIL_COLOR }}>Broke a rule {share(ch.failRate)}</span> · {outOfData ? "Ran out of data" : "Ran out of time"}{" "}
              {share(ch.stalledRate)}
            </StrategyRdStat>
            <StrategyRdStat
              label="Days to pass"
              value={ch.passRate > 0 ? days(ch.daysToPass.median) : NONE}
              unit={ch.passRate > 0 ? "median" : undefined}
            >
              {ch.passRate > 0
                ? `Half of passes took ${days(ch.daysToPass.p25)} to ${days(ch.daysToPass.p75)} days.`
                : `No start date passed within ${horizonDays} days.`}
            </StrategyRdStat>
            <StrategyRdStat
              label="Challenges per pass"
              value={ch.challengesPerPass != null ? ch.challengesPerPass.toFixed(1) : NONE}
            >
              {ch.failReasons
                ? `Fails: ${Object.entries(ch.failReasons)
                    .map(([k, v]) => `${REASON_LABELS[k] ?? k} ${v}`)
                    .join(", ")}`
                : "How many challenges you'd buy, on average, for one pass."}
            </StrategyRdStat>
            {fu && fu.accounts > 0 ? (
              <StrategyRdStat label="Paid to you · funded" value={money(fu.paid.median)} unit="median">
                Half between {money(fu.paid.p25)} and {money(fu.paid.p75)} · {share(fu.paidZeroShare)} paid nothing ·{" "}
                {money(fu.paidPerChallengeBought)} per challenge bought
              </StrategyRdStat>
            ) : null}
          </StrategyRdStatGrid>
          <p className="font-[family-name:var(--font-primary)] text-sm leading-relaxed text-muted-foreground">
            {challengeVerdict(ch, horizonDays, outOfData)}
          </p>
        </>
      ) : null}
      {pe ? (
        <StrategyRdStatGrid variant="cards" className="items-start">
          <StrategyRdStat
            label={`Result after ${horizonDays} days`}
            value={pct(pe.returnPct.median)}
            valueColor={(pe.returnPct.median ?? 0) >= 0 ? POS_COLOR : NEG_COLOR}
            unit="typical"
          >
            Half of the starts between {pct(pe.returnPct.p25)} and {pct(pe.returnPct.p75)} · worst {pct(pe.returnPct.worst)}
          </StrategyRdStat>
          <StrategyRdStat label="Ended below start" value={share(pe.belowStartShare)} unit="of start dates">
            {pe.worstStart ? `Worst start: ${formatTradeDateTimeForDisplay(pe.worstStart, timeZone)}` : "Starting on a bad week happens."}
          </StrategyRdStat>
          <StrategyRdStat
            label="Deepest dip on the way"
            value={pct(pe.maxDrawdownPct.median == null ? null : -pe.maxDrawdownPct.median)}
            valueColor={NEG_COLOR}
            unit="typical"
          >
            How far below its high the account fell during a run · worst of all {pct(pe.maxDrawdownPct.worst == null ? null : -pe.maxDrawdownPct.worst)}
          </StrategyRdStat>
          {killSwitchPct ? (
            <StrategyRdStat
              label={`Kill switch hit · ${killSwitchPct}%`}
              value={share(killedShare)}
              valueColor={killedShare > 0 ? NEG_COLOR : undefined}
              unit="of start dates"
            >
              The account fell {killSwitchPct}% from its high and stopped trading for good.
            </StrategyRdStat>
          ) : (
            <StrategyRdStat label="Fell over 30%" value={share(pe.drawdownOver30Share)} unit="of start dates">
              No kill switch set · over 50%: {share(pe.drawdownOver50Share)}
            </StrategyRdStat>
          )}
        </StrategyRdStatGrid>
      ) : null}
      {skipped.length > 0 ? (
        <p className="font-[family-name:var(--font-secondary)] text-[11px] text-muted-foreground">
          Trades skipped per run: {skipped.map(([k, v]) => `${SKIP_LABELS[k] ?? k} ${v.toFixed(0)}`).join(" · ")}
        </p>
      ) : null}
    </div>
  );
}
